import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/feedback/alert-dialog';
import { Shield, Brain, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AI_CONSENT_KEY = 'ai_data_consent_accepted';

/** Check if the user has already accepted AI data sharing consent. */
export function hasAIDataConsent(): boolean {
  try {
    return localStorage.getItem(AI_CONSENT_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Record that the user accepted AI data sharing consent. */
export function setAIDataConsent(accepted: boolean): void {
  try {
    if (accepted) {
      localStorage.setItem(AI_CONSENT_KEY, 'true');
    } else {
      localStorage.removeItem(AI_CONSENT_KEY);
    }
  } catch {
    // Storage unavailable
  }
}

interface AIDataConsentDialogProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

export function AIDataConsentDialog({ open, onAccept, onDecline }: AIDataConsentDialogProps) {
  const navigate = useNavigate();

  const handleAccept = () => {
    setAIDataConsent(true);
    onAccept();
  };

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onDecline(); }}>
      <AlertDialogContent className="max-w-md border-white/[0.08] bg-[rgba(12,16,24,0.95)] backdrop-blur-[16px] text-foreground">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg border border-[#00FFC8]/20 bg-[#00FFC8]/5">
              <Shield className="h-5 w-5 text-[#00FFC8]" />
            </div>
            <AlertDialogTitle className="text-base font-semibold">
              AI Coaching Data Usage
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-4 text-sm text-muted-foreground">
              <p>
                AthleteSpace uses AI to provide personalized coaching. To do this, some of your data
                is sent to our AI service provider for processing.
              </p>

              <div className="rounded-md border border-white/[0.08] bg-white/[0.02] p-3 space-y-2.5">
                <div className="flex items-start gap-2">
                  <Brain className="h-4 w-4 text-[#00FFC8] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-foreground mb-1">Data sent to OpenAI</p>
                    <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                      <li>Your training profile (sport, goals, availability)</li>
                      <li>Recent workout history and performance metrics</li>
                      <li>Fitness and fatigue indicators (training load)</li>
                      <li>Messages you send to the coach</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-white/[0.08] bg-white/[0.02] p-3 space-y-1.5">
                <p className="text-xs font-medium text-foreground">How your data is protected</p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Data is encrypted in transit</li>
                  <li>OpenAI does not use your data to train their models</li>
                  <li>Data is not stored by OpenAI beyond the request</li>
                  <li>You can revoke consent at any time in Settings</li>
                </ul>
              </div>

              <button
                type="button"
                onClick={() => navigate('/privacy')}
                className="inline-flex items-center gap-1 text-xs text-[#00FFC8]/70 hover:text-[#00FFC8] transition-colors"
              >
                Read our full Privacy Policy
                <ExternalLink className="h-3 w-3" />
              </button>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2 mt-2">
          <AlertDialogCancel
            onClick={onDecline}
            className="border-white/[0.08] bg-white/[0.03] text-muted-foreground hover:text-foreground"
          >
            Not Now
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleAccept}
            className="border-[#00FFC8]/25 bg-[#00FFC8]/[0.08] text-[#00FFC8] hover:bg-[#00FFC8]/15"
          >
            I Agree
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
